import React, { useState, useRef, useEffect, useCallback, useLayoutEffect } from 'react';
import './NotesBox.css';

const NotesBox = ({ 
  notes, 
  onUpdateNotes, 
  onSaveNotes, 
  onDownloadNotes, 
  onClose,
  isSaving = false,
  lastSaved,
  pdfTitle = 'untitled',
  containerBounds = null
}) => {
  
  // =========================================================
  // 1. STATE & REFS
  // =========================================================
  const [isDocked, setIsDocked] = useState(true);
  const [rect, setRect] = useState({ x: 0, y: 0, width: 300, height: 400 });
  const [isDragging, setIsDragging] = useState(false);
  const [isResizing, setIsResizing] = useState(false);
  const [isBold, setIsBold] = useState(false);
  const [isBulletList, setIsBulletList] = useState(false);
  const [isNumberedList, setIsNumberedList] = useState(false);
  
  const dragStartRef = useRef({ x: 0, y: 0 });
  const prevBoundsRef = useRef(containerBounds);
  const editorRef = useRef(null);
  const boxRef = useRef(null); // Added ref to track the box element
  const autoSaveTimeoutRef = useRef(null);

  // =========================================================
  // 2. LOAD/SAVE STATE
  // =========================================================
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem(`notesbox_config_${pdfTitle}`);
      if (saved) {
        const config = JSON.parse(saved);
        setIsDocked(config.isDocked);
        if (!config.isDocked) {
          setRect(config.rect);
        }
      }
    } catch (e) { console.warn("Load failed", e); }
  }, [pdfTitle]);

  useEffect(() => {
    const config = { isDocked, rect };
    sessionStorage.setItem(`notesbox_config_${pdfTitle}`, JSON.stringify(config));
  }, [isDocked, rect, pdfTitle]);

  // =========================================================
  // 3. ANTI-DRIFT LOGIC
  // =========================================================
  useLayoutEffect(() => {
    if (!containerBounds) return;

    if (isDocked) {
      const targetW = Math.min(300, Math.floor(containerBounds.width * 0.25));
      setRect({
        x: containerBounds.width - targetW,
        y: 0,
        width: targetW,
        height: containerBounds.height
      });
    } 
    else if (prevBoundsRef.current) {
      const prev = prevBoundsRef.current;
      const curr = containerBounds;
      const deltaX = curr.left - prev.left;
      
      setRect(currentRect => {
        let newX = currentRect.x - deltaX;
        let newY = currentRect.y;
        
        const maxX = curr.width - currentRect.width;
        const maxY = curr.height - currentRect.height;
        
        return {
          ...currentRect,
          x: Math.max(0, Math.min(newX, maxX)),
          y: Math.max(0, Math.min(newY, maxY)),
          height: Math.max(200, Math.min(currentRect.height, curr.height))
        };
      });
    }
    prevBoundsRef.current = containerBounds;
  }, [containerBounds, isDocked]);

  // =========================================================
  // 4. DRAG/RESIZE HANDLERS (MOUSE & TOUCH)
  // =========================================================
  
  // Helper to get coordinates
  const getClientPos = (e) => {
    const source = e.touches ? e.touches[0] : e;
    return {
      clientX: source.clientX,
      clientY: source.clientY
    };
  };

  const handleDragStart = (e) => {
    // Prevent dragging if clicking buttons/toolbar
    if (e.target.closest('.notes-header-controls') || e.target.closest('.notes-toolbar')) return;
    
    // Switch to floating mode on interaction
    if (isDocked) setIsDocked(false);

    setIsDragging(true);
    
    const { clientX, clientY } = getClientPos(e);
    
    // Use boxRef to ensure we calculate from the container's top-left, 
    // even if the event was triggered by the header.
    const boxRect = boxRef.current.getBoundingClientRect();
    
    dragStartRef.current = {
      x: clientX - boxRect.left,
      y: clientY - boxRect.top
    };
  };

  const handleResizeStart = (e) => {
    e.stopPropagation();
    if (isDocked) setIsDocked(false);
    setIsResizing(true);
  };

  const handleGlobalMove = useCallback((e) => {
    if (!containerBounds) return;

    // Prevent scrolling on touch devices while dragging/resizing
    if (e.cancelable && (isDragging || isResizing) && e.type === 'touchmove') {
      e.preventDefault();
    }

    const { clientX, clientY } = getClientPos(e);

    if (isDragging) {
      let newX = clientX - containerBounds.left - dragStartRef.current.x;
      let newY = clientY - containerBounds.top - dragStartRef.current.y;

      const maxX = containerBounds.width - rect.width;
      const maxY = containerBounds.height - rect.height;

      setRect(prev => ({
        ...prev,
        x: Math.max(0, Math.min(newX, maxX)),
        y: Math.max(0, Math.min(newY, maxY))
      }));
    }

    if (isResizing) {
      const newWidth = (clientX - containerBounds.left) - rect.x;
      const newHeight = (clientY - containerBounds.top) - rect.y;

      const maxWidth = containerBounds.width - rect.x;
      const maxHeight = containerBounds.height - rect.y;

      setRect(prev => ({
        ...prev,
        width: Math.max(300, Math.min(newWidth, maxWidth)),
        height: Math.max(200, Math.min(newHeight, maxHeight))
      }));
    }
  }, [isDragging, isResizing, containerBounds, rect.x, rect.y, rect.width, rect.height]);

  const handleGlobalUp = useCallback(() => {
    setIsDragging(false);
    setIsResizing(false);
    updateFormattingState();
  }, []);

  // Attach Global Listeners
  useEffect(() => {
    if (isDragging || isResizing) {
      window.addEventListener('mousemove', handleGlobalMove);
      window.addEventListener('mouseup', handleGlobalUp);
      
      // Touch: passive: false required to prevent scrolling
      window.addEventListener('touchmove', handleGlobalMove, { passive: false });
      window.addEventListener('touchend', handleGlobalUp);

      return () => {
        window.removeEventListener('mousemove', handleGlobalMove);
        window.removeEventListener('mouseup', handleGlobalUp);
        window.removeEventListener('touchmove', handleGlobalMove);
        window.removeEventListener('touchend', handleGlobalUp);
      };
    }
  }, [isDragging, isResizing, handleGlobalMove, handleGlobalUp]);

  // =========================================================
  // 5. EDITOR FUNCTIONS
  // =========================================================
  const updateFormattingState = useCallback(() => {
    if (!editorRef.current) return;
    setIsBold(document.queryCommandState('bold'));
    setIsBulletList(document.queryCommandState('insertUnorderedList'));
    setIsNumberedList(document.queryCommandState('insertOrderedList'));
  }, []);

  const handleContentChange = useCallback(() => {
    if (!editorRef.current) return;
    const newContent = editorRef.current.innerHTML;
    onUpdateNotes(newContent);
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    autoSaveTimeoutRef.current = setTimeout(() => {
      try { localStorage.setItem(`notes_${pdfTitle}`, newContent); } catch (e) {}
    }, 500);
  }, [onUpdateNotes, pdfTitle]);

  const handleEditorClick = useCallback((e) => {
    if (e.target === editorRef.current) {
      const lastChild = editorRef.current.lastElementChild;
      if (!lastChild) { editorRef.current.focus(); return; }
      const rect = lastChild.getBoundingClientRect();
      if (e.clientY > rect.bottom) {
        e.preventDefault(); 
        const lines = Math.max(1, Math.min(Math.floor((e.clientY - rect.bottom) / 24), 50));
        for (let i = 0; i < lines; i++) document.execCommand('insertParagraph', false, null);
        updateFormattingState();
        editorRef.current.focus();
      }
    }
  }, [updateFormattingState]);

  const handlePaste = useCallback((e) => {
    e.preventDefault();
    const text = (e.clipboardData || window.clipboardData).getData('text/plain');
    if (text) {
        const lines = text.split('\n').map(l => `<div>${l}</div>`).join('');
        document.execCommand('insertHTML', false, lines);
    }
    handleContentChange();
  }, [handleContentChange]);

  const handleBold = () => { document.execCommand('bold'); updateFormattingState(); handleContentChange(); };
  const handleBulletList = () => { document.execCommand('insertUnorderedList'); updateFormattingState(); handleContentChange(); };
  const handleNumberedList = () => { document.execCommand('insertOrderedList'); updateFormattingState(); handleContentChange(); };
  
  const handleSave = async () => { 
    if (autoSaveTimeoutRef.current) clearTimeout(autoSaveTimeoutRef.current);
    await onSaveNotes(editorRef.current?.innerHTML || notes);
  };

  useEffect(() => {
    if (editorRef.current && notes && editorRef.current.innerHTML !== notes) {
      editorRef.current.innerHTML = notes;
    }
  }, [notes]);

  useEffect(() => {
    const handleSel = () => setTimeout(updateFormattingState, 0);
    document.addEventListener('selectionchange', handleSel);
    return () => document.removeEventListener('selectionchange', handleSel);
  }, [updateFormattingState]);

  if (!containerBounds) return null;

  // =========================================================
  // 6. RENDER
  // =========================================================
  return (
    <div 
      ref={boxRef} // Reference needed for position calc
      className={`notes-container ${isDocked ? 'notes-docked' : 'notes-floating'}`}
      style={{
        position: 'absolute',
        left: `${rect.x}px`,
        top: `${rect.y}px`,
        width: `${rect.width}px`,
        height: `${rect.height}px`
      }}
      // TOUCH: Drag from anywhere on the body
      onTouchStart={handleDragStart} 
      // MOUSE: Removed onMouseDown here to allow text selection
    >
      {/* Header */}
      <div 
        className="notes-header"
        // MOUSE: Drag only from the header
        onMouseDown={handleDragStart}
      >
        <div className="notes-header-left">
          <span className="notes-title">📝 Take Notes</span>
          <span className="notes-last-saved">{lastSaved ? `Saved: ${new Date(lastSaved).toLocaleTimeString()}` : 'Not saved'}</span>
        </div>
        <div className="notes-header-controls">
          <button className="notes-download-btn" onClick={onDownloadNotes} title="Download PDF">⬇️</button>
          <button className="notes-save-btn" onClick={handleSave} disabled={isSaving} title="Save">
            {isSaving ? '💾...' : '💾'}
          </button>
          <button className="notes-close-btn" onClick={onClose} title="Close">✕</button>
        </div>
      </div>

      {/* Toolbar */}
      <div className="notes-toolbar">
        <button className={`format-button ${isBold ? 'format-button-active' : ''}`} onClick={handleBold} title="Bold"><strong>B</strong></button>
        <button className={`format-button ${isBulletList ? 'format-button-active' : ''}`} onClick={handleBulletList} title="List">• List</button>
        <button className={`format-button ${isNumberedList ? 'format-button-active' : ''}`} onClick={handleNumberedList} title="List">1. List</button>
      </div>

      {/* Content */}
      <div className="notes-content">
        <div
          ref={editorRef}
          className="notes-editor"
          contentEditable
          onInput={handleContentChange}
          onMouseUp={updateFormattingState}
          onKeyUp={updateFormattingState}
          onPaste={handlePaste}
          onClick={handleEditorClick}
          placeholder="Type here..."
          suppressContentEditableWarning={true}
        />
      </div>

      {/* Resize Handle */}
      <div 
        className="notes-resize-handle" 
        onMouseDown={handleResizeStart} // Mouse Resize
        onTouchStart={handleResizeStart} // Touch Resize
      />
    </div>
  );
};

export default NotesBox;